import { useState, useEffect, useCallback } from "react";

/**
 * Generic hook for loading options from an API with transformation
 * @param {Function} apiCall - Function that returns a promise with API data
 * @param {Function} mapper - Function to transform API response to options format
 * @param {any} trigger - Dependency that triggers the API call (e.g., isOpen)
 * @param {Object} config - Configuration options
 * @param {boolean} config.loadOnMount - Whether to load immediately on mount (default: false)
 * @param {boolean} config.loadOnce - Whether to only load once (default: true)
 * @returns {Object} { options, isLoading, error, refetch }
 */
const useOptions = (apiCall, mapper, trigger = null, config = {}) => {
  const { loadOnMount = false, loadOnce = true } = config;

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchOptions = useCallback(async () => {
    // Skip if already loaded and loadOnce is true
    if (loadOnce && hasLoaded) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiCall();
      const transformedOptions = mapper ? mapper(response) : response;
      setOptions(transformedOptions);
      setHasLoaded(true);
    } catch (err) {
      console.error("Failed to load options:", err);
      setError(err);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiCall, mapper, loadOnce, hasLoaded]);

  const refetch = useCallback(() => {
    setHasLoaded(false);
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    // Load on mount if configured to do so
    if (loadOnMount) {
      fetchOptions();
    }
  }, [loadOnMount, fetchOptions]);

  useEffect(() => {
    // Load when trigger becomes truthy (e.g., when modal opens)
    if (trigger && !loadOnMount && !hasLoaded) {
      fetchOptions();
    }
  }, [trigger, fetchOptions, loadOnMount, hasLoaded]);

  return {
    options,
    isLoading,
    error,
    refetch
  };
};

export default useOptions;
